/**
 * Slug-qualified endpoint URL composition (task 385).
 *
 * Every AI Service endpoint is addressed as `/api/{accountSlug}/{path}` — the
 * account's slug namespaces its path so two accounts can both own `summarize`.
 * This module is the single place that shape is spelled out; every builder and
 * display site in the app goes through it so they can never drift apart.
 *
 * **Which slug?** Always the *endpoint row's own* `accountSlug` when rendering
 * or calling a specific endpoint — that is correct even for platform (System
 * account) endpoints, which live under `mw`. The caller's own slug
 * (`AuthUser.accountSlug`, via `useAccountSlug()`) is only for create-time
 * previews and the path-input prefix adornment, where no row exists yet.
 *
 * **No frontend feature flag.** The backend rollout is flag-gated
 * (`innovate.be.endpoints.slug-paths`) with dual resolution during the
 * transition, and the app reads that state indirectly: an endpoint row that
 * carries `accountSlug` renders and is invoked in the slug-qualified shape; a
 * row without one falls back to the legacy root shape, which the backend still
 * resolves. So a separate FE flag would only be able to disagree with the data.
 */

import config from '@/config';
import { BARE_URL_ENVIRONMENT_PATH } from '@/types/environments';
import type { Endpoint } from '@/types';

/**
 * The System (Monarch Works) account's slug. Platform AI Services — the
 * Composer, UI Composer and Test Composer — are owned by that account, so they
 * are addressed exactly like any other endpoint: `/api/mw/composer`, etc. There
 * is no resolver carve-out for them.
 */
export const SYSTEM_ACCOUNT_SLUG = 'mw';

/** Endpoint-shaped input: anything carrying a path and (maybe) an owning slug. */
export type EndpointUrlParts = Pick<Endpoint, 'path'> & {
  accountSlug?: string | null;
};

/**
 * Strip leading and trailing slashes from a stored endpoint path.
 *
 * Stored paths are inconsistent — some carry a leading slash, some don't — and
 * the server now normalizes both away on create/update. Working in the bare
 * form means joins never collapse (`/api` + `foo` → `/apifoo`) or double up.
 */
export function bareEndpointPath(path: string | null | undefined): string {
  return (path ?? '').replace(/^\/+/, '').replace(/\/+$/, '');
}

/**
 * The request path the transport appends to the `/api` base, without a leading
 * slash: `{accountSlug}/{path}`, or just `{path}` when no slug is known
 * (legacy rows / pre-claim tokens).
 */
export function qualifiedEndpointPath(
  accountSlug: string | null | undefined,
  path: string | null | undefined,
  environment?: string | null
): string {
  const bare = bareEndpointPath(path);
  // `/` is an environment PATH, not a segment (task 830): it means "this
  // environment has no segment", so the address it produces is the bare one.
  // Handled here rather than at each call site so every helper below, and every
  // caller of this one, inherits it — including `services/api.ts`, which builds
  // the invoke URL for a test run.
  const segment = environment === BARE_URL_ENVIRONMENT_PATH ? null : environment;
  const withEnvironment = segment ? `${segment}/${bare}` : bare;
  return accountSlug ? `${accountSlug}/${withEnvironment}` : withEnvironment;
}

/**
 * The environment-qualified form: `/api/{accountSlug}/{env}/{path}` (task 760).
 *
 * **`environment` is the environment's PATH, never its display label** (task
 * 830): the resolver matches the URL segment against `Environment.path`, and
 * names became free text in the same change — two stages may now share one, so
 * a name cannot address anything.
 *
 * The environment segment sits between the slug and the path, and it is what
 * makes a request hit **exactly what that stage serves** — the same resolution
 * production traffic takes, not a parameter the app interprets. Passing nothing
 * addresses the working state where the account is not publishing, and where it
 * is, the released version of the environment whose path is `/`. Passing `/`
 * itself lands on that same bare
 * URL, because that is the only address such an environment has.
 *
 * Environment paths and endpoint first segments share one per-Account namespace
 * (the service enforces that at write time), which is precisely what lets one
 * path shape mean both things without ambiguity.
 */
export function environmentDisplayPath(
  endpoint: EndpointUrlParts,
  environment: string | null | undefined
): string {
  return `/${qualifiedEndpointPath(endpoint.accountSlug, endpoint.path, environment)}`;
}

/** Absolute environment-qualified URL: `${API_URL}/api/{slug}/{env}/{path}`. */
export function environmentInvokeUrl(
  endpoint: EndpointUrlParts,
  environment: string | null | undefined
): string {
  return `${apiServerUrl()}${environmentDisplayPath(endpoint, environment)}`;
}

/**
 * The invoke URL with the endpoint's own path removed:
 * `${API_URL}/api/{slug}/{env}` (or `${API_URL}/api/{slug}` with no environment).
 *
 * This exists for the OpenAPI `servers` array (task 799). A server URL is a
 * PREFIX that every path key is appended to, and the environment segment sits
 * *between* the slug and the path — so a document that offers one server per
 * environment has to carry the slug in the server too, leaving the bare endpoint
 * path as the key. Composed here rather than at the call site so the
 * environment-qualified shape stays spelled out in exactly one module.
 */
export function environmentServerUrl(
  endpoint: EndpointUrlParts,
  environment: string | null | undefined
): string {
  const prefix = qualifiedEndpointPath(endpoint.accountSlug, '', environment).replace(/\/+$/, '');
  return prefix ? `${apiServerUrl()}/${prefix}` : apiServerUrl();
}

/**
 * Display form of the callable path, with exactly one leading slash:
 * `/{accountSlug}/{path}`. Use wherever the app shows "this is the URL you
 * call" without the origin — headers, chips, copy affordances.
 */
export function endpointDisplayPath(endpoint: EndpointUrlParts): string {
  return `/${qualifiedEndpointPath(endpoint.accountSlug, endpoint.path)}`;
}

/**
 * Display form including the `/api` mount: `/api/{accountSlug}/{path}`. This is
 * the string a User can paste after the API origin.
 */
export function endpointApiPath(endpoint: EndpointUrlParts): string {
  return `/api${endpointDisplayPath(endpoint)}`;
}

/** The API origin + mount that every endpoint is served under. */
export function apiServerUrl(): string {
  return `${(config.API_URL ?? '').replace(/\/$/, '')}/api`;
}

/**
 * The absolute, public URL systems call to invoke this AI Service:
 * `${API_URL}/api/{accountSlug}/{path}`.
 */
export function endpointInvokeUrl(endpoint: EndpointUrlParts): string {
  return `${apiServerUrl()}${endpointDisplayPath(endpoint)}`;
}

/**
 * The two surfaces that hang off an endpoint's own address as a trailing
 * reserved segment (tasks 838, 845): its MCP server and its published interface
 * page. Pinned by `innovate-service/tests/unit/surfaceDispatch.test.ts`.
 */
export type EndpointSurfaceName = 'mcp' | 'ui';

/**
 * THE CUSTOM-DOMAIN ADDRESS OF AN AI SERVICE (tasks 850, 857).
 *
 * One rule, stated once, for every surface the app shows a URL on: **the public
 * URL is the internal URL minus the `/api/{slug}` prefix.** On the platform host
 * the account is named by the slug in the path; on a custom domain it is named
 * by the hostname, and the edge adds that prefix back on the way in — so the
 * whole difference between the two shapes is that prefix.
 *
 *     internal   ${API_URL}/api/acme/staging/summarize/mcp
 *     public     https://ai.acme.com/staging/summarize/mcp
 *
 * The environment segment rides between the two halves and is untouched, which
 * includes the bare-URL environment (`/`): that path means "this environment has
 * no segment", so it produces the address with no segment at all rather than a
 * doubled slash — `qualifiedEndpointPath` is what already knows that, which is
 * exactly why this composes through it.
 *
 * Always `https`: a customer hostname is only ever reachable through the managed
 * certificate, so there is no other form to offer.
 */
export function customDomainUrl(
  endpoint: EndpointUrlParts,
  hostname: string,
  options: { environment?: string | null; surface?: EndpointSurfaceName } = {}
): string {
  // No slug: the hostname IS the account, and the edge supplies the prefix.
  const path = qualifiedEndpointPath(null, endpoint.path, options.environment);
  return `https://${hostname}/${path}${options.surface ? `/${options.surface}` : ''}`;
}

/**
 * The custom-domain counterpart of `environmentServerUrl` — the same URL with
 * the endpoint's own path removed, for the OpenAPI `servers` array (task 857):
 * `https://{hostname}` , or `https://{hostname}/{env}` when a stage is named.
 *
 * The slug is absent for the same reason it is absent above, which is what makes
 * this a prefix the bare endpoint path can be appended to — the shape the
 * document's path key already has whenever it carries environment servers.
 */
export function customDomainServerUrl(
  hostname: string,
  environment?: string | null
): string {
  const prefix = qualifiedEndpointPath(null, '', environment).replace(/\/+$/, '');
  return prefix ? `https://${hostname}/${prefix}` : `https://${hostname}`;
}

/**
 * The public page the AI Service's published interface is served at
 * (tasks 847, 850) — `{invocation URL}/ui`, because a surface always sits one
 * segment past its endpoint.
 *
 * Two shapes, one rule: on the platform host the internal path is spelled out in
 * full (`${API_URL}/api/{slug}/{path}/ui`), and on a custom domain it is that
 * path minus the `/api/{slug}` prefix — which is `customDomainUrl`'s whole job,
 * so this defers to it rather than restating the rule (task 857).
 */
export function endpointUiUrl(
  endpoint: EndpointUrlParts,
  customDomainHostname?: string | null,
  environment?: string | null
): string {
  if (customDomainHostname) {
    return customDomainUrl(endpoint, customDomainHostname, { environment, surface: 'ui' });
  }
  return `${apiServerUrl()}/${qualifiedEndpointPath(
    endpoint.accountSlug,
    endpoint.path,
    environment
  )}/ui`;
}

/**
 * The platform counterpart of `customDomainUrl` for a surface:
 * `${API_URL}/api/{slug}/{env?}/{path}/{surface}`. `endpointUiUrl` is the `ui`
 * case of this and predates it; this one exists because MCP needs the same
 * shape and neither should spell it out twice.
 */
export function endpointSurfaceUrl(
  endpoint: EndpointUrlParts,
  surface: EndpointSurfaceName,
  environment?: string | null
): string {
  return `${apiServerUrl()}/${qualifiedEndpointPath(
    endpoint.accountSlug,
    endpoint.path,
    environment
  )}/${surface}`;
}
